import React, { useState, useEffect } from 'react';
import { Container, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';

const Profile = () => {
  const { user, profile, signOut, updateProfile } = useAuth();
  const [fullName, setFullName] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [profileLoading, setProfileLoading] = useState(true);

  // Load profile data
  useEffect(() => {
    if (user && profile) {
      setFullName(profile.full_name || '');
      setUsername(profile.username || '');
      setEmail(user.email || '');
      setProfileLoading(false);
    } else if (user) {
      // If we have user but no profile yet, wait a bit
      const timeout = setTimeout(() => {
        setProfileLoading(false);
      }, 2000);
      return () => clearTimeout(timeout);
    }
  }, [user, profile]);

  // Update profile
  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    
    // Reset status messages
    setError('');
    setSuccess('');
    
    // Validate password fields if attempting to change password
    if (newPassword) {
      if (!currentPassword) {
        return setError('Current password is required to change your password');
      }
      
      if (newPassword !== confirmPassword) {
        return setError('New passwords do not match');
      }
      
      if (newPassword.length < 6) {
        return setError('Password must be at least 6 characters');
      }
    }
    
    try {
      setLoading(true);
      
      // Update profile using Supabase
      const { data, error } = await updateProfile({
        full_name: fullName,
        username
      });
      
      if (error) {
        throw error;
      }
      
      // Handle password change separately if needed
      if (newPassword) {
        const { supabase } = await import('../config/supabase');
        const { error: passwordError } = await supabase.auth.updateUser({
          password: newPassword
        });
        
        if (passwordError) {
          throw new Error('Failed to update password: ' + passwordError.message);
        }
        
        toast.success('Password updated successfully');
      }
      
      setSuccess('Profile updated successfully');
      toast.success('Profile updated successfully');
      
      // Reset password fields after successful update
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      
    } catch (err) {
      setError(err.message || 'Failed to update profile');
      console.error('Error updating profile:', err);
    } finally {
      setLoading(false);
    }
  };

  if (profileLoading) {
    return (
      <Container className="d-flex justify-content-center my-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  return (
    <Container>
      <h2 className="mb-4">Your Profile</h2>
      
      <Card>
        <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          {success && <Alert variant="success">{success}</Alert>}
          
          <Form onSubmit={handleUpdateProfile}>
            <h5 className="mb-3">Profile Information</h5>
            
            <Form.Group className="mb-3">
              <Form.Label>Full Name</Form.Label>
              <Form.Control
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Enter your full name"
                required
              />
              <Form.Text className="text-muted">
                Your full name as it appears in the system
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Username</Form.Label>
              <Form.Control
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
              <Form.Text className="text-muted">
                Display name used in the system
              </Form.Text>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                value={email}
                disabled
                readOnly
              />
              <Form.Text className="text-muted">
                Email cannot be changed (used for login)
              </Form.Text>
            </Form.Group>
            
            <h5 className="mt-4 mb-3">Change Password</h5>
            <p className="text-muted">Leave blank if you don't want to change your password</p>
            
            <Form.Group className="mb-3">
              <Form.Label>Current Password</Form.Label>
              <Form.Control
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>New Password</Form.Label>
              <Form.Control
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Confirm New Password</Form.Label>
              <Form.Control
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
              />
            </Form.Group>
            
            <Button
              type="submit"
              variant="primary"
              className="mt-3"
              disabled={loading}
            >
              {loading ? 'Updating...' : 'Update Profile'}
            </Button>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default Profile;
